import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";

import { Button, HookSelectFormGroup, CloseButton } from "components";
import { ACHProcessorBoardingFields, DebounceTimeout } from "globalConstants";
import { useGetAchProcessorDetailsQuery } from "queries";
import {
	AchBoardingStatusTypes,
	BoardingCompleteOutcomes,
	BoardingOutcomeEnum,
	StraddleAccountStatusTypes,
} from "types/enums";
import { EnumItem } from "types/enums/customEnum";
import { required } from "validators/validation";

import CheckListRow from "../components/checkListRow";

import styles from "../modal.module.scss";

type CompleteBoardingOutcomeModalProps = {
	show?: boolean;
	onHide: () => void;
	onSubmit: Function;
	boardingDetails: {
		mid?: string;
		discovery_mid?: string;
		open_pend_count: number;
		has_any_not_closed_pends: boolean;
	};
	isTsysProcessor: boolean;
	applicationId: string;
	achProcessor: string;
};

const CompleteBoardingOutcomeModal = ({
	show = false,
	onHide,
	onSubmit,
	boardingDetails,
	isTsysProcessor,
	applicationId,
	achProcessor,
}: CompleteBoardingOutcomeModalProps) => {
	const [showChecklist, setShowChecklist] = useState<boolean>(false);

	const formMethods = useForm({
		mode: "onChange",
		reValidateMode: "onChange",
	});

	const {
		handleSubmit,
		formState: { errors, isDirty, isValid },
		setError,
		clearErrors,
	} = formMethods;

	const handleSubmitForm = values => {
		onHide();
		onSubmit(values?.boardingOutcome);
	};

	const { data: achData, isFetching: isAchDataFetching } =
		useGetAchProcessorDetailsQuery(applicationId, achProcessor);

	const isAchDataExist =
		achData?.[ACHProcessorBoardingFields.AchBoardingStatus.name]?.value ===
			AchBoardingStatusTypes.Rejected.value ||
		(achData?.[ACHProcessorBoardingFields.StraddleIdentifier.name] &&
			achData?.[ACHProcessorBoardingFields.StraddleGateId.name] &&
			achData?.[ACHProcessorBoardingFields.StraddleGateKeyId.name] &&
			achData?.[ACHProcessorBoardingFields.StraddleAccountStatus.name] ===
				StraddleAccountStatusTypes.Active.displayName);

	const isAchProcessor = !!achProcessor;

	const outcomes: EnumItem[] = BoardingCompleteOutcomes;

	const isMidExist = boardingDetails?.mid;
	const isDiscoverMidExist = boardingDetails?.discovery_mid;
	const isOpenPends = boardingDetails?.has_any_not_closed_pends;

	const canCompleteApplication =
		isMidExist &&
		!isOpenPends &&
		(isTsysProcessor ? isDiscoverMidExist : true) &&
		(isAchProcessor ? isAchDataExist : true);

	const getCheckList = () => {
		const midRow = (
			<CheckListRow title="MID Assigned" active={isMidExist} />
		);

		const discoverMidRow = (
			<CheckListRow
				title="Discover MID Assigned"
				active={isDiscoverMidExist}
			/>
		);
		const pendsRow = (
			<CheckListRow title="Pends Resolved" active={!isOpenPends} />
		);
		const achDataRow = (
			<CheckListRow title="ACH Data Provided" active={isAchDataExist} />
		);

		return isAchDataFetching ? null : (
			<ul className={styles.checkList}>
				{midRow}
				{isTsysProcessor && discoverMidRow}
				{isAchProcessor && achDataRow}
				{pendsRow}
			</ul>
		);
	};

	const handleChangeOutcome = ({ name }) => {
		if (name === BoardingOutcomeEnum.SuccessfulBoarding.name) {
			setShowChecklist(true);
			!canCompleteApplication &&
				setError("notRegisteredInput", {
					message:
						"To change application status provide MID, Discover MID, ACH Data and close pends",
				});
		} else {
			setShowChecklist(false);
			clearErrors("notRegisteredInput");
		}
	};

	return (
		<FormProvider {...formMethods}>
			<Modal
				show={show}
				onHide={onHide}
				animation={false}
				centered
				className={styles.wrapUWDecisionModal}
			>
				<form onSubmit={handleSubmit(handleSubmitForm)}>
					<Modal.Header className="pb-0">
						<Modal.Title as="h6" className="text-center mb-3">
							<div>Complete Boarding</div>
						</Modal.Title>
						<CloseButton onClick={onHide} />
					</Modal.Header>
					<Modal.Body>
						<div className={styles.content}>
							<HookSelectFormGroup
								rules={{
									validate: {
										required,
									},
								}}
								label="Boarding Outcome"
								options={outcomes}
								getOptionLabel={option => option.displayName}
								getOptionValue={option => option.name}
								placeholder="Please Select"
								name="boardingOutcome"
								cacheOptions
								defaultOptions
								debounceTimeout={DebounceTimeout}
								colLabel={{ sm: 12 }}
								colSelect={{ sm: 12 }}
								showRequiredMark
								onChange={handleChangeOutcome}
							/>
							{showChecklist && <div>{getCheckList()}</div>}
						</div>
					</Modal.Body>
					<Modal.Footer className="justify-content-center pt-0 pb-5">
						<Button
							type="submit"
							value="Complete"
							variant="primary"
							disabled={
								!isDirty ||
								!isValid ||
								!!Object.keys(errors).length
							}
						/>
					</Modal.Footer>
				</form>
			</Modal>
		</FormProvider>
	);
};

export default CompleteBoardingOutcomeModal;
